<template>
  <div class="search">
    <div
      v-if="showOverlay"
      class="search__overlay"
      @click="showOverlay = false"
    ></div>

    <label class="search__label" for="input">
      <search-icon class="search__icon" />
      <span class="search__inputs">
        <input
          ref="searchInput"
          v-model="value"
          type="text"
          class="search__input"
          placeholder="Zoeken"

          @focus="onFocus"
          @blur="onBlur"

          @input="getPlaces"
          @keyup="onKeyPress"
        />
        <span ref="valueElement" class="search__value">{{ value }}</span>
        <span class="search__suggester" :style="{'left': suggesterPosition}">
          {{ exampleValue }}
        </span>
      </span>
    </label>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import SearchIcon from '@atoms/search-icon';

export default {
  name: 'SearchBar',

  components: {
    SearchIcon,
  },

  props: {
    autoFocus: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      isMounted: false,
      value: '',
      exampleValue: '',
      suggestion: '',
      suggesterPosition: '0px',
      showOverlay: false,
      places: [],
      timeout: null,
      states: {
        small: '0,25000',
        medium: '25000,50000',
        normal: '50000,100000',
        large: '100000,20000000',
      },
      stateLabels: {
        '0,25000': '< 25.000',
        '25000,50000': '25.000 - 50.000',
        '50000,100000': '50.000 - 100.000',
        '100000,20000000': '> 100.000',
      },
    };
  },

  computed: {
    ...mapState({
      map: (state) => state.google.map,
      selectedYear: (state) => state.rawData.year,
    }),

    ...mapGetters({
      sepData: 'rawData/allSepData',
    }),
  },

  mounted() {
    this.isMounted = true;

    if (this.autoFocus) {
      this.$refs.searchInput.focus();
    }
  },

  methods: {
    onFocus() {
      this.showOverlay = true;

      this.$root.$emit('dataLayer/reset');
      this.$store.commit('infoBoxCounter/setContinueToNextState', false);
      this.$store.commit('infoBoxCounter/setActiveItem', null);
      this.$store.commit('infoBoxCounter/stop');
      this.$root.$emit('dataLayer/set');
    },

    getPlaces() {
      clearTimeout(this.timeout);
      this.places = [];

      if (!this.value) {
        this.places = [];
        this.exampleValue = '';
        this.suggestion = '';

        return;
      }

      this.timeout = setTimeout(() => {
        const checkValue = this.value.toLowerCase();

        this.map.data.forEach((feature) => {
          const city = feature.getProperty('statnaam').toLowerCase();

          if (city.indexOf(checkValue) === 0) {
            this.places.push(feature);
          }
        });

        if (!this.places.length) {
          this.suggestion = '';
          this.exampleValue = '';

          return;
        }

        const suggest = this.places[0].getProperty('statnaam').substr(checkValue.length);

        this.suggestion = this.value + suggest;
        this.exampleValue = suggest;

        if (this.exampleValue) {
          const { valueElement } = this.$refs;
          const width = valueElement.clientWidth;

          this.suggesterPosition = `${width + 3}px`;
        }
      }, 100);
    },

    onKeyPress(event) {
      const { key } = event;

      if (key === 'ArrowRight') {
        this.value = this.suggestion;

        this.getPlaces();
      }

      if (key === 'Enter') {
        this.value = this.suggestion;

        this.setFeature();

        this.showOverlay = false;

        this.$emit('submit');
      }
    },

    onBlur() {
      this.showOverlay = false;

      if (this.suggestion) {
        this.value = this.suggestion;

        this.setFeature();

        this.$emit('submit');
      }
    },

    setFeature() {
      this.$refs.searchInput.blur();

      let feature = null;

      const checkValue = this.value.toLowerCase();
      this.map.data.forEach((f) => {
        const city = f.getProperty('statnaam').toLowerCase();

        if (city === checkValue) {
          feature = f;
        }
      });

      if (feature === null) {
        return;
      }

      let regionSize = '';

      Object.keys(this.sepData).forEach((year) => {
        Object.keys(this.sepData[year]).forEach((key) => {
          this.sepData[year][key].forEach((item) => {
            if (item.Gemeente.toLowerCase() === checkValue) {
              regionSize = key;
            }
          });
        });
      });

      if (regionSize) {
        const state = this.states[regionSize];
        const label = this.stateLabels[state];
        this.$store.commit('filter/changed', {
          key: 'Inwoners',
          type: 'RANGE',
          label,
          value: [state],
        });
      }

      this.$nextTick(() => {
        this.$store.commit('dataLayer/setSelectedRegion', feature.getProperty('statnaam'));
        this.$store.commit('dataLayer/setFeature', feature);

        this.value = '';
        this.suggestion = '';
        this.exampleValue = '';

        this.showOverlay = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  .search {
    &__label {
      align-items: center;
      background-color: $color-white;
      border-radius: $global-radius;
      box-shadow: 0 4px 10px rgba($color-silver, .5);
      display: flex;
      height: 50px;
      justify-content: flex-start;
      padding: 0 $global-spacing-unit-threequarter;
      position: relative;
      width: 100%;

      @include mq($from: tablet) {
        height: 60px;
        margin: 0 auto;
        width: 700px;
      }
    }

    &__icon {
      fill: $color-lynch;
      flex-shrink: 0;
      margin-right: $global-spacing-unit-threequarter;
      width: 28px;
    }

    &__inputs {
      display: block;
      position: relative;
      width: 100%;
    }

    &__value {
      @include font-size(theta);

      align-items: center;
      display: flex;
      height: 24px;
      left: 3px;
      opacity: 0;
      position: absolute;
      top: 0;
    }

    &__suggester {
      @include font-size(theta);

      align-items: center;
      color: $color-dove-gray;
      display: flex;
      font-style: italic;
      height: 24px;
      position: absolute;
      top: 50%;
      transform: translateX(-3px) translateY(-50%);
    }

    &__input {
      @include font-size(theta);

      border: 0;
      font-family: $font-family-base;
      outline: none;
      padding: 0;
      position: relative;
      width: 100%;

      &::placeholder {
        font-style: italic;
      }
    }

    &__overlay {
      background-color: rgba($color-dove-gray, .67);
      height: 100%;
      left: 0;
      position: fixed;
      top: 0;
      width: 100%;

      @include mq($from: tablet) {
        display: none;
      }
    }
  }
</style>
